import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack } from "@mui/material";
import { useTu } from "@src/appV2/i18n/useTu";
import { type UseModalState } from "@src/lib/ui-react";

import { Dialog } from "../../components/Dialog";
import { DialogFooter } from "../../components/DialogFooter";
import { Text } from "../../components/Text";

export interface RemoveChangeOfOwnershipConfirmationDialogProps {
  modalState: UseModalState;
  /** Name of the workplace whose change of ownership is being removed. */
  originName: string;
  isRemoving: boolean;
  errorMessage?: string;
  onConfirm: () => void;
}

/**
 * Confirmation step for removing a recorded change of ownership. Rendered by
 * ChangeOfOwnershipDialog on top of the update dialog; expects a Berlin theme ancestor.
 */
export function RemoveChangeOfOwnershipConfirmationDialog(
  props: RemoveChangeOfOwnershipConfirmationDialogProps
) {
  const { modalState, originName, isRemoving, errorMessage, onConfirm } = props;
  const { tu } = useTu("changeOfOwnership");

  return (
    <Dialog
      modalState={modalState}
      title={tu("Remove change of ownership?")}
      footerContent={
        <DialogFooter
          primaryAction={{
            label: tu("Remove"),
            variant: "dangerFilled",
            isLoading: isRemoving,
            onClick: onConfirm,
          }}
          secondaryAction={{
            label: tu("Cancel"),
            disabled: isRemoving,
            onClick: modalState.closeModal,
          }}
        />
      }
    >
      <Stack spacing={3}>
        <Text variant="body2">
          {tu(
            "This removes the recorded new owner and effective date from {{name}}. You can record a new change of ownership later.",
            { values: { name: originName } }
          )}
        </Text>
        {isDefined(errorMessage) && <Alert severity="error">{errorMessage}</Alert>}
      </Stack>
    </Dialog>
  );
}
